/**
 * Add Outcomes — the modal an operator fills a reel from (CAE-1553).
 *
 * Spec: `08-modals/README.md` → "B · Add Outcomes modal"; values from `.m-*` in
 * [`prototype/modals.css`](../../../../../../docs/design/admin-ui/prototype/modals.css).
 *
 * It replaces `reel-outcome-picker-embedded.tsx` and the reel-mode branch of
 * `<CataloguePage>` — a flow that took one fixture, then one market, then one
 * outcome, and added them one at a time. This one is a grouped multi-select:
 * fixture → market → outcomes, with a single confirm.
 *
 * **It writes nothing.** Confirming hands the caller ready-to-create picks and
 * Edit › Reels posts them, which is where every other reel mutation already
 * lives (`use-reel-option-mutations.ts`). That seam is also why the one
 * request per outcome is not this file's problem.
 *
 * > **The filters are a sidebar, not a strip.** CAE-1553's description and
 * > `08-modals/README.md` both say "light filter strip: fixture, market
 * > category, search". `shared/modal-add-outcomes.jsx` passes `ModalShell`'s
 * > `aside` — Search, Competition, Sport, Market type, Status, and a toggle —
 * > and `modals.css` draws `.m-modal-body-split`. The same contradiction
 * > CAE-1551 settled the same way: the stylesheet and the component win, and
 * > the handoff's screenshot is that sidebar below its 960px collapse.
 *
 * > **The fixture facet is not a chip row.** The README asks for "multi-select
 * > chips of the contest's attached fixtures". The component has no such
 * > control, and it would duplicate the list underneath it — every attached
 * > fixture is already a row the operator can collapse. Search and the market
 * > facet narrow the same set with one idiom instead of two.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import type { Theme } from '@mui/material/styles';
import {
  catalogueApi,
  type CatalogueLocale,
  type CatalogueMarket,
  type CatalogueOutcome,
} from '../../../../api/catalogue';
import type { ContestDetailFixture } from '../../../../api/contests';
import { marketsQueryKey } from '../../../../components/catalogue';
import {
  AdminEmptySection,
  ModalContext,
  ModalFootSummary,
  ModalHost,
  NoFixturesIcon,
  NoResultsIcon,
  PlusIcon,
} from '../../../../components/ui';
import { toOutcomePick, type OutcomePick } from './add-outcomes-picks';
import {
  activeFilters,
  EMPTY_FILTERS,
  fixtureIsVisible,
  fixtureMatchesSearch,
  toFacets,
  visibleMarkets,
  type AddOutcomesFilterState,
} from './add-outcomes-filters';
import { AddOutcomesSidebar } from './add-outcomes-sidebar';
import { FixturePickRow } from './fixture-pick-row';
import { resolveFixtureLabel } from '@s2w/core';
import { FilterChip } from '../../../../components/ui';
import { formatSportLabel } from '../../../../utils/sport-label';
import { formatInteger } from '../../../../utils/number';

const CATALOGUE_STALE_MS = 60_000;
const LOCALE: CatalogueLocale = 'en';

/** `.m-list` / `.m-listhead` inset. The body is `flush` so each band sets its own. */
const LIST_PADDING_X = 28;

export interface AddOutcomesModalProps {
  open: boolean;
  /** The reel being added to, as the head's context chip names it. */
  reelLabel: string;
  /**
   * The contest's **attached** fixtures, and only those.
   *
   * CAE-1553's acceptance criterion: an operator builds a reel from the
   * fixtures the contest already carries, never from the whole catalogue. The
   * picker it replaced browsed the feed, which let a reel be built on a fixture
   * the contest had no row for — the create endpoint then made one silently.
   */
  fixtures: readonly ContestDetailFixture[];
  /** Outcome external ids already a Reel Option on this reel. */
  reelOutcomeIds: readonly string[];
  /** A create is in flight. The confirm stays mounted and says so. */
  isPending: boolean;
  onClose: () => void;
  onConfirm: (picks: OutcomePick[]) => void;
}

export const AddOutcomesModal = ({
  open,
  reelLabel,
  fixtures,
  reelOutcomeIds,
  isPending,
  onClose,
  onConfirm,
}: AddOutcomesModalProps) => {
  /*
   * The picks hold the whole triple, not just outcome ids.
   *
   * The create endpoint takes the fixture, the market and the outcome together,
   * and the market a chosen outcome belongs to is only known while its card is
   * mounted — a filter that hides the fixture would take the answer with it. So
   * the payload is built at tick time and kept, which is the same reason the
   * Add Fixtures modal holds fixture records rather than ids.
   */
  const [picks, setPicks] = useState<ReadonlyMap<string, OutcomePick>>(new Map());
  const [filters, setFilters] = useState<AddOutcomesFilterState>(EMPTY_FILTERS);
  const [expandedFixtures, setExpandedFixtures] = useState<ReadonlySet<string>>(new Set());
  const [openMarkets, setOpenMarkets] = useState<ReadonlySet<string>>(new Set());
  /** Markets ticked while collapsed, whose outcomes are still in flight. */
  const [pendingBulk, setPendingBulk] = useState<ReadonlySet<string>>(new Set());

  const queryClient = useQueryClient();
  const onReelIds = useMemo(() => new Set(reelOutcomeIds), [reelOutcomeIds]);
  const selectedIds = useMemo(() => new Set(picks.keys()), [picks]);

  /*
   * Every open starts clean.
   *
   * `ModalHost` takes `open` rather than being conditionally rendered, so this
   * component is mounted for the life of the screen and its state outlives a
   * dismissal. Without this, reopening after a successful add offers the
   * outcomes just added — still ticked, because the picks are held by id and
   * nothing recomputes them against the reel — and confirming re-posts every one
   * of them at a reel that now holds them. CAE-1553's criterion is that an
   * already-added outcome cannot be selected or double-added, and a dismissal
   * discarding the selection is the Add Fixtures modal's rule too; that one gets
   * it by remounting on `useAttachFixtures`' `sessionKey`, which this screen has
   * no equivalent of.
   */
  useEffect(() => {
    if (!open) return;
    setPicks(new Map());
    setFilters(EMPTY_FILTERS);
    setExpandedFixtures(new Set());
    setOpenMarkets(new Set());
    setPendingBulk(new Set());
  }, [open]);

  /*
   * Every attached fixture's markets, in parallel, on the drill-down's own
   * cache key so nothing is fetched twice.
   *
   * Eager, unlike the outcomes below, and the asymmetry is the point. The
   * grouped list, the market facet's options and the search over market names
   * all need the market *names*, which no count endpoint carries — and the
   * request is bounded by how many fixtures a contest has attached (a handful),
   * not by the catalogue's 253. The outcomes are the fan-out worth avoiding: a
   * single fixture carries up to 141 markets.
   */
  /*
   * Folded here rather than in a `useMemo` over the results array, which
   * `useQueries` rebuilds on every render — a memo keyed on it would never hit,
   * and keying on a join of the data is the kind of cache key that silently
   * stops working. `combine` is the hook's own answer to this.
   *
   * It has to be a *stable* function to be that answer. `useQueries` reruns
   * `combine` whenever its identity changes, and the result here is a `Map` and
   * two `Set`s, which `replaceEqualDeep` cannot structurally share — so an
   * inline arrow hands back a new `marketsByFixture` on every render and defeats
   * the `facets` and `listed` memos that depend on it, which is the miss this
   * was supposed to avoid.
   */
  const combineMarkets = useCallback(
    (
      results: Array<{
        data?: CatalogueMarket[];
        isPending: boolean;
        isSuccess: boolean;
        isError: boolean;
      }>,
    ) => ({
      marketsByFixture: new Map<string, CatalogueMarket[]>(
        fixtures.map((fixture, index) => [fixture.externalId, results[index]?.data ?? []]),
      ),
      /*
       * Per fixture, because the rows resolve independently and an expanded row
       * must not claim "no markets synced" — which sends the operator off to run
       * a sync — while its own request is still in flight.
       */
      marketsPending: new Set<string>(
        fixtures
          .filter((_fixture, index) => results[index]?.isPending ?? true)
          .map((fixture) => fixture.externalId),
      ),
      /**
       * Whose markets never arrived. An expanded row must not say "no markets
       * synced" — which sends the operator off to run a sync — over a request
       * that failed; the sync was never the problem there.
       */
      marketsFailed: new Set<string>(
        fixtures
          .filter((_fixture, index) => results[index]?.isError ?? false)
          .map((fixture) => fixture.externalId),
      ),
      /** Until every fixture has answered, a fixture is judged on its own fields. */
      marketsLoaded: results.every((result) => result.isSuccess || result.isError),
    }),
    [fixtures],
  );

  const { marketsByFixture, marketsLoaded, marketsPending, marketsFailed } = useQueries({
    queries: fixtures.map((fixture) => ({
      queryKey: marketsQueryKey(fixture.externalId, LOCALE),
      queryFn: () => catalogueApi.listMarketsForFixture(fixture.externalId, LOCALE),
      enabled: open,
      staleTime: CATALOGUE_STALE_MS,
    })),
    combine: combineMarkets,
  });

  const setPick = (
    fixture: ContestDetailFixture,
    market: CatalogueMarket,
    outcome: CatalogueOutcome,
    selected: boolean,
  ) =>
    setPicks((previous) => {
      const next = new Map(previous);
      if (selected) next.set(outcome.externalId, toOutcomePick(fixture, market, outcome));
      else next.delete(outcome.externalId);
      return next;
    });

  const setBulk = (
    fixture: ContestDetailFixture,
    market: CatalogueMarket,
    outcomes: CatalogueOutcome[],
    selected: boolean,
    loaded: boolean,
  ) => {
    /*
     * A tick on a market whose outcomes have not arrived: remember it, let the
     * card fetch them, and apply when they land. Untick clears the intent, so
     * a fast double-tick does not select a market the operator has let go of.
     *
     * Keyed on `loaded`, not on the list being empty. A market whose every
     * outcome is already on the reel also offers an empty list, and treating
     * that as "still loading" left it parked here for the rest of the session
     * with its outcomes query pinned on.
     */
    if (selected && !loaded) {
      setPendingBulk((previous) => new Set(previous).add(market.externalId));
      return;
    }
    setPendingBulk((previous) => {
      if (!previous.has(market.externalId)) return previous;
      const next = new Set(previous);
      next.delete(market.externalId);
      return next;
    });
    setPicks((previous) => {
      const next = new Map(previous);
      for (const outcome of outcomes) {
        if (selected) next.set(outcome.externalId, toOutcomePick(fixture, market, outcome));
        else next.delete(outcome.externalId);
      }
      return next;
    });
  };

  /**
   * The picks that are still worth sending.
   *
   * A pick the reel has since taken is not a pick, however it got there. The
   * batch is one request per outcome and carries on past a failure, so a partly
   * successful add leaves the modal open over a selection whose landed half is
   * now on the reel; re-posting those is the duplicate the server would reject.
   * Filtering here rather than pruning `picks` keeps the operator's selection
   * intact if a fixture is detached and re-attached underneath them.
   */
  const confirmable = useMemo(
    () => [...picks.values()].filter((pick) => !onReelIds.has(pick.externalOutcomeId)),
    [picks, onReelIds],
  );

  const count = confirmable.length;
  const marketCount = new Set(confirmable.map((pick) => pick.externalMarketId)).size;
  /*
   * The foot's second clause. `08-modals/README.md:90` and CAE-1553's own Scope
   * both write the summary as "N outcome(s) selected from M market(s)" and stop
   * there, but `screenshots/02-add-outcomes.png` reads "across 1 market · 1
   * fixture" — and the fixture is the half worth having, because picking across
   * fixtures is the whole point of a grouped list. The stylesheet does not
   * settle it either way: `.m-foot-meta` is a dot-separated row of however many
   * clauses it is given. Logged in `prototype/README.md`.
   */
  const fixtureCount = new Set(confirmable.map((pick) => pick.fixture.externalId)).size;

  const facets = useMemo(() => toFacets(fixtures, marketsByFixture), [fixtures, marketsByFixture]);
  const setFilter = (next: Partial<AddOutcomesFilterState>) =>
    setFilters((previous) => ({ ...previous, ...next }));
  const chips = activeFilters(filters, setFilter, (code) => formatSportLabel(code) ?? code);

  /**
   * The rows on screen, and the markets drawn inside each.
   *
   * Computed here rather than in the row, because the fixture's own visibility
   * depends on whether anything inside it survived — a row that opens onto no
   * markets is a worse answer than a shorter list.
   */
  const listed = useMemo(
    () =>
      fixtures
        .map((fixture) => {
          const label = resolveFixtureLabel(fixture);
          const markets = marketsByFixture.get(fixture.externalId) ?? [];
          return {
            fixture,
            label,
            markets: visibleMarkets(
              markets,
              filters,
              fixtureMatchesSearch(fixture, label, filters.search),
            ),
            visible: fixtureIsVisible(fixture, label, markets, filters, marketsLoaded),
          };
        })
        .filter((entry) => entry.visible),
    [fixtures, marketsByFixture, filters, marketsLoaded],
  );

  /**
   * `.m-listhead-of` — what the operator has to pick from, summed over the rows
   * actually listed.
   *
   * The contest payload's own counts, which is the call the per-row
   * `MarketBadge` already makes: `markets.length` is the *filtered* list and is
   * empty while the fetch is in flight, so a header built from it would open
   * reading "0 markets" over fixtures the payload already knows carry 141. The
   * two numbers agree with the badges beside them because they come from the
   * same place.
   */
  const listedTotals = useMemo(
    () =>
      listed.reduce(
        (totals, { fixture }) => ({
          markets: totals.markets + fixture.marketCount,
          outcomes: totals.outcomes + fixture.outcomeCount,
        }),
        { markets: 0, outcomes: 0 },
      ),
    [listed],
  );

  /** `.m-pickbadge` on each fixture row — how many of its outcomes are picked. */
  const pickedByFixture = useMemo(() => {
    const byFixture = new Map<string, number>();
    for (const pick of confirmable) {
      const id = pick.fixture.externalId;
      byFixture.set(id, (byFixture.get(id) ?? 0) + 1);
    }
    return byFixture;
  }, [confirmable]);

  return (
    <ModalHost
      open={open}
      onClose={onClose}
      title="Add outcomes"
      subtitle="Pick from the markets on attached fixtures. Selecting a market adds all its outcomes — toggle individual ones below."
      context={
        <ModalContext lead="Adding to" id={reelLabel} name={summariseReel(reelOutcomeIds)} />
      }
      flush
      aside={<AddOutcomesSidebar filters={filters} onChange={setFilter} facets={facets} />}
      summary={
        <ModalFootSummary
          count={count}
          label={count === 1 ? 'outcome selected' : 'outcomes selected'}
          meta={
            count > 0
              ? [
                  `across ${marketCount} ${marketCount === 1 ? 'market' : 'markets'}`,
                  `${fixtureCount} ${fixtureCount === 1 ? 'fixture' : 'fixtures'}`,
                ]
              : undefined
          }
        />
      }
      actions={
        <>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<PlusIcon />}
            disabled={count === 0 || isPending}
            onClick={() => onConfirm(confirmable)}
          >
            {isPending
              ? 'Adding…'
              : `Add ${count > 0 ? `${count} ` : ''}${count === 1 ? 'outcome' : 'outcomes'}`}
          </Button>
        </>
      }
    >
      {chips.length > 0 ? (
        <Box
          sx={(theme: Theme) => ({
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px',
            padding: `10px ${LIST_PADDING_X}px`,
            backgroundColor: theme.tokens.color.surfaceMuted,
            borderBottom: theme.tokens.border.default,
          })}
        >
          <Box
            component="span"
            sx={(theme: Theme) => ({
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: theme.tokens.color.ink4,
            })}
          >
            Active filters
          </Box>
          {chips.map((chip) => (
            <FilterChip
              key={chip.filterKey}
              filterKey={chip.filterKey}
              value={chip.value}
              onRemove={chip.clear}
            />
          ))}
          <Button
            variant="text"
            size="small"
            /*
             * Hard right, as `02-add-outcomes.png` draws it — the chips grow
             * from the left and the escape from them sits at the far edge, so
             * it stays in one place however many are active rather than sliding
             * along behind the last chip.
             */
            sx={{ marginLeft: 'auto' }}
            onClick={() => setFilters({ ...EMPTY_FILTERS, hideOnReel: filters.hideOnReel })}
          >
            Clear all
          </Button>
        </Box>
      ) : null}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          padding: `${LIST_PADDING_X / 2}px ${LIST_PADDING_X}px ${LIST_PADDING_X}px`,
        }}
      >
        {listed.length > 0 ? (
          /* `.m-listhead` — the count, what it is drawn from, and the two bulk toggles. */
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              paddingBottom: '2px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', minWidth: 0 }}>
              <Box
                component="h3"
                sx={(theme: Theme) => ({
                  margin: 0,
                  fontSize: '15px',
                  fontWeight: 600,
                  letterSpacing: '-0.005em',
                  color: theme.tokens.color.ink,
                })}
              >
                {`Fixtures (${listed.length})`}
              </Box>
              <Box
                component="span"
                sx={(theme: Theme) => ({
                  fontSize: '12.5px',
                  color: theme.tokens.color.ink4,
                  fontVariantNumeric: 'tabular-nums',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                })}
              >
                {`${formatInteger(listedTotals.markets)} ${
                  listedTotals.markets === 1 ? 'market' : 'markets'
                } · ${formatInteger(listedTotals.outcomes)} ${
                  listedTotals.outcomes === 1 ? 'outcome' : 'outcomes'
                } available`}
              </Box>
            </Box>

            {/*
             * Fixtures only. Expanding a row draws its market heads, which is
             * one request each; expanding every market under them would be the
             * 141-outcome fan-out the whole screen is built to avoid.
             */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
              <Button
                variant="text"
                size="small"
                disabled={listed.every(({ fixture }) => expandedFixtures.has(fixture.externalId))}
                onClick={() =>
                  setExpandedFixtures(new Set(listed.map(({ fixture }) => fixture.externalId)))
                }
              >
                Expand all
              </Button>
              <Button
                variant="text"
                size="small"
                disabled={listed.every(({ fixture }) => !expandedFixtures.has(fixture.externalId))}
                onClick={() => setExpandedFixtures(new Set())}
              >
                Collapse all
              </Button>
            </Box>
          </Box>
        ) : null}

        {listed.length === 0 ? (
          /*
           * Which emptiness this is *can* be read off the fixture list here,
           * unlike the Add Fixtures modal: this modal's whole source is the
           * contest's attached fixtures, so none attached and none surviving the
           * filters are different facts about different things. Telling an
           * operator who has not been to Edit › Fixtures yet to widen a search
           * would point them away from the only screen that can help.
           */
          fixtures.length === 0 ? (
            <AdminEmptySection
              icon={<NoFixturesIcon />}
              title="No fixtures attached to this contest"
              subtitle="A reel is built from the markets on attached fixtures. Attach some on Edit › Fixtures, then come back to pick outcomes from them."
            />
          ) : (
            <AdminEmptySection
              icon={<NoResultsIcon />}
              title="No fixtures match these filters"
              subtitle="Widen the search, or clear a filter to see more of the contest’s fixtures."
            />
          )
        ) : null}

        {listed.map(({ fixture, markets }) => (
          <FixturePickRow
            key={fixture.externalId}
            fixture={fixture}
            markets={markets}
            locale={LOCALE}
            expanded={expandedFixtures.has(fixture.externalId)}
            onExpandedChange={(next) =>
              setExpandedFixtures((previous) => toggleInSet(previous, fixture.externalId, next))
            }
            openMarkets={openMarkets}
            onMarketOpenChange={(marketId, next) =>
              setOpenMarkets((previous) => toggleInSet(previous, marketId, next))
            }
            pendingBulk={pendingBulk}
            selectedIds={selectedIds}
            onReelIds={onReelIds}
            hideOnReel={filters.hideOnReel}
            marketsPending={marketsPending.has(fixture.externalId)}
            marketsFailed={marketsFailed.has(fixture.externalId)}
            onRetryMarkets={() =>
              void queryClient.invalidateQueries({
                queryKey: marketsQueryKey(fixture.externalId, LOCALE),
              })
            }
            pickedHere={pickedByFixture.get(fixture.externalId) ?? 0}
            onOutcomeToggle={(market, outcome, next) => setPick(fixture, market, outcome, next)}
            onBulkSelect={(market, outcomes, next, loaded) =>
              setBulk(fixture, market, outcomes, next, loaded)
            }
          />
        ))}
      </Box>
    </ModalHost>
  );
};

const toggleInSet = (previous: ReadonlySet<string>, id: string, present: boolean) => {
  const next = new Set(previous);
  if (present) next.add(id);
  else next.delete(id);
  return next;
};

/** The context chip's second clause — what the reel already holds. */
const summariseReel = (reelOutcomeIds: readonly string[]): string =>
  reelOutcomeIds.length === 1 ? '1 outcome already' : `${reelOutcomeIds.length} outcomes already`;
